import { type FC } from 'react'
import pluralize from 'pluralize'
import { Spinner } from 'react-bootstrap'
import { formatDate } from '../../common/dateUtils'
import { formatCurrencyAmount } from '../../common/intlFormatters'
import { seatTypeLabels } from './roleChangeSeatImpact'
import { type SeatPurchasePreview } from './useSeatPurchasePreview'

export interface SeatPurchaseSummaryProps {
  preview: SeatPurchasePreview
}

const amount = (cents: number, currency: string) => formatCurrencyAmount(cents / 100, currency)

const DUE_TITLES = { today: 'Due today', endOfTrial: 'Due at end of trial' }

export const SeatPurchaseSummary: FC<SeatPurchaseSummaryProps> = ({ preview }) => {
  const { lines, newRecurringFee, period, renewsOn, dueLabel, dueAmount, currency, isLoading } = preview
  const periodSuffix = period === 'year' ? 'yr' : 'mo'
  const feeTitle = period === 'year' ? 'New annual fee' : 'New monthly fee'
  const dueValue = dueAmount === null ? null : amount(dueAmount, currency)

  return (
    <div className='d-flex flex-column'>
      {lines.map((line) => (
        <div key={line.seatType} className='d-flex justify-content-between align-items-center border-bottom zocks-border-color-gray-300 py-3'>
          <div className='d-flex flex-column'>
            <span className='paragraph-medium'>
              <span className='text-capitalize'>{seatTypeLabels[line.seatType]}</span> {pluralize('seat', line.quantity)}
            </span>
            {line.unitAmount === null ? (
              <span className='paragraph-small zocks-text-gray-500'>
                {line.quantity} {pluralize('seat', line.quantity)}
              </span>
            ) : (
              <span className='paragraph-small zocks-text-gray-500'>
                {line.quantity}x {amount(line.unitAmount, currency)}/seat/{periodSuffix}
              </span>
            )}
          </div>
          {line.unitAmount === null ? null : <span className='paragraph-medium'>{amount(line.unitAmount * line.quantity, currency)}</span>}
        </div>
      ))}
      {newRecurringFee === null ? null : (
        <div className='d-flex justify-content-between align-items-center py-3'>
          <div className='d-flex flex-column'>
            <span className='paragraph-medium bold'>{feeTitle}</span>
            {renewsOn ? <span className='paragraph-small zocks-text-gray-500'>from {formatDate(new Date(renewsOn))}</span> : null}
          </div>
          <span className='paragraph-medium bold'>{amount(newRecurringFee, currency)}</span>
        </div>
      )}
      {dueLabel === null ? null : (
        <div className='d-flex justify-content-between align-items-center rounded-2 zocks-bg-color-gray-100 border zocks-border-color-gray-300 px-3 py-3'>
          <span className='paragraph-medium bold'>{DUE_TITLES[dueLabel]}</span>
          {dueValue === null ? isLoading ? <Spinner size='sm' /> : null : <span className='paragraph-medium bold'>{dueValue}</span>}
        </div>
      )}
    </div>
  )
}
